import { useEffect, useState, type ReactNode } from 'react';
import { ActionIcon, Alert, Anchor, Box, Breadcrumbs, Button, Group, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useConsoleNav } from '../shell/nav';
import { AutoGrid, ChipRail, ColLabel, ConsoleTable, EstatePill, Pill, Row, SectionCard, StatSkeleton, TableMessage } from '../ui';
import { clockMs, dateOnly, num } from '../ui/format';
import { tokens as T } from '../theme/tokens';
import { useEstatesStore, useMemoriesStore, useTenantsStore } from '../stores/dataStores';
import { isEmptyResult, isFirstLoad, isUnconfigured, rowsOf } from '../stores/collection';
import { useUIStore } from '../stores/uiStore';
import { memoryFilterDirty, memoryQuery, memoryTitle, parseScope } from '../data/memoryState';
import { tenantKey, type Memory } from '../data/types';
import { MemoryDrawer } from '../features/memory/MemoryDrawer';
import { RecallPanel } from '../features/memory/RecallPanel';
import { useMemoryStats } from '../features/memory/useMemoryStats';

const COLS = [
  { key: 'updated', label: 'Updated', w: 130 },
  { key: 'scope', label: 'Scope', w: 180 },
  { key: 'kind', label: 'Kind', w: 100 },
  { key: 'text', label: 'Title' },
  { key: 'tags', label: 'Tags', w: 170 },
  { key: 'source', label: 'Source', w: 140 },
  { key: 'status', label: 'Status', w: 100 },
  { key: 'v', label: 'v', w: 52 },
];

const KINDS = ['', 'fact', 'preference', 'decision', 'lesson', 'profile', 'episode'] as const;
const STATUSES = ['active', 'superseded', 'deleted', ''] as const;

/** The memory browser for one tenant (DR-75): a page at a time through the
 *  admin API under the ledger's cursor-stack pattern, a detail drawer, and
 *  the recall debugger. Every cell that shows agent-written content is a
 *  text node. */
export function MemoryPage() {
  const { estateId = '', tenantId = '' } = useParams();
  const navigate = useConsoleNav();
  const key = tenantKey(estateId, tenantId);
  const estates = useEstatesStore((s) => s.data);
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  const memories = useMemoriesStore((s) => s.data);
  const load = useMemoriesStore((s) => s.load);
  const ui = useUIStore();
  const [statsTick, setStatsTick] = useState(0);
  const stats = useMemoryStats(estateId, tenantId, statsTick);
  const [extra, setExtra] = useState<Record<string, Memory>>({});
  const [textDraft, setTextDraft] = useState(ui.memoryFilter.text);

  const cursor = ui.memoryPage > 0 ? (ui.memoryCursors[ui.memoryPage - 1] ?? '') : '';
  useEffect(() => {
    load(memoryQuery(estateId, tenantId, ui.memoryFilter, ui.memoryPageSize, cursor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estateId, tenantId, ui.memoryFilter, ui.memoryPageSize, cursor]);

  // The text filter is debounced: every keystroke would otherwise be a
  // request to the admin API.
  useEffect(() => {
    const t = setTimeout(() => {
      if (textDraft !== ui.memoryFilter.text) ui.setMemoryFilter({ text: textDraft });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textDraft]);

  const estate = estates.byId[estateId];
  const tenant = tenants.find((t) => t.key === key);
  const rows = rowsOf(memories).filter((m) => m.estateId === estateId && m.tenantId === tenantId);
  const dirty = memoryFilterDirty(ui.memoryFilter);
  const memoryOn = estate?.memory?.mode === 'on';

  const refreshStats = () => setStatsTick((n) => n + 1);
  const onForgotten = memories.gen; // a forget upserts a row; recount on the next render
  useEffect(() => {
    refreshStats();
  }, [onForgotten]);

  return (
    <Stack gap={16}>
      <Breadcrumbs separator="/" styles={{ separator: { color: T.ink4, marginInline: 8 } }}>
        <Anchor c={T.ink4} fw={700} fz={12} onClick={() => navigate('/estates')}>
          Estates
        </Anchor>
        <Anchor c={T.ink4} fw={700} fz={12} onClick={() => navigate(`/estates/${estateId}`)}>
          {estate?.displayName ?? estateId}
        </Anchor>
        <Anchor c={T.ink4} fw={700} fz={12} onClick={() => navigate(`/estates/${estateId}/tenants/${tenantId}`)}>
          {tenant?.displayName ?? tenantId}
        </Anchor>
        <Text fz={12} fw={700} c={T.ink}>
          Memory
        </Text>
      </Breadcrumbs>

      <Group justify="space-between" align="center" gap={20} wrap="wrap">
        <Group gap={14} wrap="nowrap" style={{ minWidth: 0 }}>
          <ActionIcon variant="default" radius={999} size={36} onClick={() => navigate(`/estates/${estateId}/tenants/${tenantId}`)}>
            ←
          </ActionIcon>
          <Title order={2}>Memory</Title>
          <EstatePill estate={estateId} size="xl" />
          <Text ff="monospace" fz={13} c={T.ink5}>
            {key}
          </Text>
        </Group>
        <Text fz={12} c={T.ink4}>
          Reindex and purge are CLI: <code>haus memory reindex|purge --estate {estateId} --tenant {tenantId}</code>
        </Text>
      </Group>

      {estate && !memoryOn ? (
        <Alert color="orange" radius="lg" title="Memory is off for this estate">
          Every /v1/memory/* call for {estateId} answers 403. Rows below, if any, are inert. Turn it on with{' '}
          <code>haus memory set --estate {estateId} --mode on --apply</code>.
        </Alert>
      ) : null}

      <AutoGrid min={140} gap={12}>
        <Stat label="Active" value={statValue(stats, (s) => num(s.active))} />
        <Stat label="Superseded" value={statValue(stats, (s) => num(s.superseded))} />
        <Stat label="Forgotten (tombstones)" value={statValue(stats, (s) => num(s.deleted))} />
        <Stat label="Loaded on this page" value={num(rows.length)} small />
      </AutoGrid>

      <SectionCard p="14px 18px">
        <Group gap={12} wrap="wrap" align="flex-end">
          <Box>
            <ColLabel mb={6}>Kind</ColLabel>
            <ChipRail
              value={ui.memoryFilter.kind as (typeof KINDS)[number]}
              onChange={(v) => ui.setMemoryFilter({ kind: v })}
              size="sm"
              data={KINDS.map((k) => ({ value: k, label: k === '' ? 'All' : k }))}
            />
          </Box>
          <Box>
            <ColLabel mb={6}>Status</ColLabel>
            <ChipRail
              value={ui.memoryFilter.status as (typeof STATUSES)[number]}
              onChange={(v) => ui.setMemoryFilter({ status: v })}
              size="sm"
              data={STATUSES.map((s) => ({ value: s, label: s === '' ? 'all' : s }))}
            />
          </Box>
          <TextInput
            label="Scope"
            value={ui.memoryFilter.scope}
            onChange={(e) => ui.setMemoryFilter({ scope: e.currentTarget.value })}
            placeholder="repo:… / user:… / global"
            style={{ flex: '0 1 220px' }}
            styles={{ input: { fontFamily: T.fontMono } }}
          />
          <TextInput
            label="Tag"
            value={ui.memoryFilter.tag}
            onChange={(e) => ui.setMemoryFilter({ tag: e.currentTarget.value })}
            placeholder="exact tag"
            style={{ flex: '0 1 160px' }}
            styles={{ input: { fontFamily: T.fontMono } }}
          />
          <TextInput
            label="Text contains"
            value={textDraft}
            onChange={(e) => setTextDraft(e.currentTarget.value)}
            placeholder="substring, this page"
            style={{ flex: '1 1 220px' }}
          />
          {dirty ? (
            <Button
              variant="default"
              onClick={() => {
                setTextDraft('');
                ui.clearMemoryFilters();
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </Group>
      </SectionCard>

      {memories.error && !isUnconfigured(memories) ? (
        <Alert color="red" radius="lg" title="The last load failed">
          {memories.error}
        </Alert>
      ) : null}

      <ConsoleTable cols={COLS} minWidth={980}>
        {isUnconfigured(memories) ? (
          <TableMessage span={COLS.length} title="Not connected" detail="Memory has no admin route on this instance (ROUTER_MEMORY_ENABLED is off), so there is nothing to read." />
        ) : isFirstLoad(memories) ? (
          <TableMessage span={COLS.length} title="Loading…" />
        ) : isEmptyResult(memories) || rows.length === 0 ? (
          <TableMessage
            span={COLS.length}
            title={dirty ? 'No memories match these filters' : 'No memories'}
            detail={dirty ? undefined : 'Nothing has been remembered for this tenant yet.'}
          />
        ) : (
          rows.map((m) => (
            <Row key={m.id} onClick={() => ui.selectMemory(m.id)} selected={ui.memorySelection === m.id}>
              <Table.Td>
                <Text fz={12} c={T.ink3} title={m.updatedAt}>
                  {dateOnly(m.updatedAt)} {clockMs(m.updatedAt)}
                </Text>
              </Table.Td>
              <Table.Td>
                <ScopeCell scope={m.scope} />
              </Table.Td>
              <Table.Td>
                <Pill tone="neutral" size="sm">
                  {m.kind || 'fact'}
                </Pill>
              </Table.Td>
              <Table.Td>
                <Text fz={13} truncate style={{ maxWidth: 520 }}>
                  {memoryTitle(m)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  {(m.tags ?? []).slice(0, 3).map((t) => (
                    <Pill key={t} tone="neutral" size="sm">
                      {t}
                    </Pill>
                  ))}
                  {(m.tags?.length ?? 0) > 3 ? (
                    <Text fz={11} c={T.ink4}>
                      +{(m.tags?.length ?? 0) - 3}
                    </Text>
                  ) : null}
                </Group>
              </Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <Pill tone={m.source.actor === 'user' ? 'ok' : 'neutral'} size="sm">
                    {m.source.actor || 'agent'}
                  </Pill>
                  <Text ff="monospace" fz={11} c={T.ink4} truncate style={{ maxWidth: 70 }}>
                    {m.source.ref}
                  </Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Pill tone={m.status === 'active' ? 'ok' : m.status === 'deleted' ? 'fail' : 'neutral'} size="sm">
                  {m.status}
                </Pill>
              </Table.Td>
              <Table.Td>
                <Text ff="monospace" fz={12}>
                  {m.version}
                </Text>
              </Table.Td>
            </Row>
          ))
        )}
      </ConsoleTable>

      <Group justify="space-between" align="center">
        <Text fz={12} c={T.ink4}>
          Page {ui.memoryPage + 1}
          {stats.phase === 'ready' ? ` · ${num(stats.stats.active)} active in the store` : ''}
          {rows.some((m) => !m.indexed) ? ' · some rows await the index' : ''}
        </Text>
        <Group gap={8}>
          <Button variant="default" disabled={ui.memoryPage === 0} onClick={() => ui.prevMemoryPage()}>
            ← Previous
          </Button>
          <Button variant="default" disabled={!memories.nextCursor} onClick={() => memories.nextCursor && ui.nextMemoryPage(memories.nextCursor)}>
            Next →
          </Button>
        </Group>
      </Group>

      <RecallPanel estateId={estateId} tenantId={tenantId} onHits={(byId) => setExtra((e) => ({ ...e, ...byId }))} />
      <MemoryDrawer extra={extra} />
    </Stack>
  );
}

function statValue(stats: ReturnType<typeof useMemoryStats>, pick: (s: { active: number; superseded: number; deleted: number }) => string): ReactNode {
  switch (stats.phase) {
    case 'ready':
      return pick(stats.stats);
    case 'pending':
      return <StatSkeleton />;
    case 'unconfigured':
      return '—';
    default:
      return '—';
  }
}

function ScopeCell({ scope }: { scope: string }) {
  const p = parseScope(scope);
  if (!('id' in p)) {
    return (
      <Pill tone="neutral" size="sm">
        global
      </Pill>
    );
  }
  return (
    <Group gap={6} wrap="nowrap">
      <Pill tone="neutral" size="sm">
        {p.kind}
      </Pill>
      <Text ff="monospace" fz={12} truncate style={{ maxWidth: 110 }}>
        {p.id}
      </Text>
    </Group>
  );
}

function Stat({ label, value, small }: { label: string; value: ReactNode; small?: boolean }) {
  return (
    <SectionCard p="14px 16px">
      <Text fz={12} c={T.ink3}>
        {label}
      </Text>
      <Text component="div" fz={small ? 16 : 22} fw={800} mt={2}>
        {value}
      </Text>
    </SectionCard>
  );
}

